import { IonButton, IonButtons, IonContent, IonFab, IonFabButton, IonHeader, IonIcon, IonPage, IonTitle, IonToolbar } from "@ionic/react";
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./CustomCamera.css";
import { isNil } from 'lodash';
import { syncOutline } from "ionicons/icons";


interface CustomCameraProps {
	onDismiss(photo: string): void;
}

type CameraFacingMode = "user" | "environment";

function CustomCamera({ onDismiss }: CustomCameraProps) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [cameraFacingMode, setCameraFacingMode] = useState<CameraFacingMode>("environment");

	const { t } = useTranslation();

	const startCapture = useCallback(async () => {
		try {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => {
					track.stop();
				});
			}
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: cameraFacingMode,
				}
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}
		} catch (error) {
			console.error("Error accessing camera:", error);
		}
	}, [cameraFacingMode]);

	const closeStreams = useCallback(() => {
		if (!streamRef.current) {
			onDismiss("");
			return;
		}
		streamRef.current.getTracks().forEach((track) => {
			track.stop();
		});
	}, [streamRef.current]);

	useEffect(() => {
		startCapture();

		return () => {
			closeStreams();
		}
	}, [closeStreams, startCapture]);


	function handleDismiss(photo?: string) {
		if (isNil(photo) || photo === "") {
			onDismiss("");
		} else {
			onDismiss(photo);
		}
	}

	function takePhoto() {
		if (!videoRef.current || !canvasRef.current) {
			return;
		}

		const video = videoRef.current;
		const canvas = canvasRef.current;
		const context = canvas.getContext("2d");

		if (context) {
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
			const photo = canvas.toDataURL("image/png");			
			handleDismiss(photo);
		}
	}

	async function switchCamera(event: SyntheticEvent) {
		event.preventDefault();
		const newCameraFacingMode = cameraFacingMode === "environment" ? "user" : "environment";

		streamRef.current?.getTracks().forEach((track) => {
			track.stop();
		});

		const stream = await navigator.mediaDevices.getUserMedia({
			video: {
				facingMode: newCameraFacingMode,
			}
		});

		streamRef.current = stream;
		if (videoRef.current) {
			videoRef.current.srcObject = stream;
		}

		setCameraFacingMode(newCameraFacingMode);
	}

	const openFileDialog = () => {
		(document as any).getElementById("file-upload").click();
	 };
	 
	const setImage = (_event: any) => {
	   let file = _event.target.files![0];
	   if (file) {
		const reader = new FileReader();
		reader.onloadend = () => {
			
			const base64String = reader.result as string;
			// Here you can use the base64String as needed
			handleDismiss(base64String);
			// For example, you can set it in state or pass it to another function
			
			
		};
		reader.readAsDataURL(file);
		
		
	   }
	   		
	   //console.log(file)
	 }

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						<IonButton color="medium" onClick={() => handleDismiss("")}>
							{t("general.cancel")}
						</IonButton>
					</IonButtons>
					<IonTitle>Take a photo</IonTitle>
				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen>
				<div className="overlay">
					<div className="top-right" />
					<div className="top-left" />
					<div className="bottom-right" />
					<div className="bottom-left" />

					<div className="horizontal-line"></div>
					<div className="vertical-line"></div>
					<IonFab
						horizontal="end"
						slot="fixed"
						vertical="bottom"
						>
							<IonFabButton
								onClick={switchCamera}
								size="small"
							>
								<IonIcon icon={syncOutline} size='medium' />
							</IonFabButton>
					</IonFab>
					<canvas id="canvas" ref={canvasRef} style={{ width: "0%" }} />
					<video
						ref={videoRef}
						autoPlay
						playsInline
						style={{ height: "90%", width: "auto", margin: "auto" }}
					/>
				</div>
				{/* <div className="square"></div> */}
				<IonToolbar>
					<div style={{
						display: "flex",
					}}>
					<div style={{
							flex: 1,
						}}>
					<IonButton
						color="primary"
						expand="block"
						onClick={takePhoto}
					>

						{t("customCamera.takePhoto")}
					</IonButton>
					<input   type="file"   id="file-upload"   style={{ display: "none" }}   onChange={setImage} />
					</div>
					<div style={{
							flex: 1,
						}}>
					<IonButton 
						color="primary"
						expand="block"
						onClick={openFileDialog}
					>							
  							{t("details.uploadPhoto")}
					</IonButton>
					</div>
					</div>
				</IonToolbar>
			</IonContent>
		</IonPage>
	);
};

export default CustomCamera;
